import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useFamily } from '../context/FamilyContext.jsx';
import { useQuestionnaireData } from '../context/QuestionnaireDataContext.jsx';
import { useRecoveryEmailPrompt } from '../context/RecoveryEmailPromptContext.jsx';
import { SHARE_OPTIONS, BURDEN_OPTIONS, THOUGHT_OPTIONS } from '../constants.js';
import ProgressBar from '../components/ProgressBar.jsx';
import StepSlider from '../components/StepSlider.jsx';
import { useEmbeddedMode, useEmbeddedNavigation } from '../hooks/useEmbeddedMode.js';
import Button from '../ui/Button.jsx';
import Switch from '../ui/Switch.jsx';
import { Card, CardContent } from '../ui/Card.jsx';
import useOnboardingStatus from '../hooks/useOnboardingStatus.js';
import { scrollElementBelowSticky } from '../utils/scroll.js';

export default function CategoryQuestionnairePage() {
  const { token, user } = useAuth();
  const { family, members } = useFamily();
  const { answers, ensureQuestionnaireData, error: questionnaireError, questions, setAnswers, status: questionnaireStatus } = useQuestionnaireData();
  const { maybeAutoOpenPrompt } = useRecoveryEmailPrompt();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const embedded = useEmbeddedMode();
  const { navigateEmbedded } = useEmbeddedNavigation();
  const requestedQuestionUuid = searchParams.get('question');

  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);

  const [shareScore, setShareScore] = useState(null);
  const [burdenScore, setBurdenScore] = useState(null);
  const [thoughtScore, setThoughtScore] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [notConcerned, setNotConcerned] = useState(false);
  const stickyHeaderRef = useRef(null);
  const answerCardRef = useRef(null);
  const prevScoresRef = useRef(null);

  const myMember = useMemo(
    () => (members || []).find((member) => member.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );
  const {
    error: progressError,
    hasCompletedCategory,
    refreshProgress
  } = useOnboardingStatus({
    token,
    family,
    member: myMember
  });

  const load = async () => {
    setError('');
    const { questions: loadedQuestions, answers: loadedAnswers } = await ensureQuestionnaireData();
    const answersMap = loadedAnswers || {};
    const cat = loadedQuestions?.categories?.find((c) => c.id === categoryId) || null;
    // Category hidden by the household profile (or unknown id) → back to the overview.
    if (loadedQuestions && !cat) {
      navigateEmbedded('/questionnaire');
      return;
    }
    const requestedIndex = cat && requestedQuestionUuid
      ? cat.questions.findIndex((qq) => qq.question_uuid === requestedQuestionUuid)
      : -1;
    const firstUnansweredIndex = cat ? cat.questions.findIndex((qq) => !answersMap[qq.question_uuid]) : -1;
    const initialIndex = requestedIndex >= 0
      ? requestedIndex
      : (firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0);
    // Only initialize index on load/category change, not on every answer update.
    setIndex(initialIndex);
  };

  useEffect(() => {
    setIndex(0);
    load().catch((e) => setError(e?.message || 'Failed to load category'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, requestedQuestionUuid]);

  const category = useMemo(() => {
    return questions?.categories?.find((c) => c.id === categoryId) || null;
  }, [questions, categoryId]);
  const answeredQuestionsCount = useMemo(
    () => Object.keys(answers || {}).length,
    [answers]
  );

  const currentQuestion = category?.questions?.[index] || null;
  const pageBottomPadding = embedded
    ? 'calc(6rem + 1rem)'
    : hasCompletedCategory
      ? 'calc(6rem + var(--ptcm-bottom-nav-h) + env(safe-area-inset-bottom))'
      : 'calc(6rem + env(safe-area-inset-bottom))';
  const actionBarBottom = embedded
    ? '0px'
    : hasCompletedCategory
      ? 'calc(var(--ptcm-bottom-nav-h) + env(safe-area-inset-bottom))'
      : 'env(safe-area-inset-bottom)';

  const shareSteps = useMemo(() => SHARE_OPTIONS.filter((o) => o.value !== 0), []);
  const burdenSteps = useMemo(() => BURDEN_OPTIONS.filter((o) => o.value !== 0), []);
  const thoughtSteps = useMemo(() => THOUGHT_OPTIONS.filter((o) => o.value !== -1), []);

  const isNotConcernedScores = useMemo(() => {
    return shareScore === 0 && burdenScore === 0 && thoughtScore === -1;
  }, [shareScore, burdenScore, thoughtScore]);

  // Load saved answer values when changing card
  useEffect(() => {
    if (!currentQuestion || !answers) return;
    const existing = answers[currentQuestion.question_uuid];
    setShareScore(existing?.share_score ?? null);
    setBurdenScore(existing?.burden_score ?? null);
    setThoughtScore(existing?.thought_score ?? null);
    const nc = (existing?.share_score ?? null) === 0 && (existing?.burden_score ?? null) === 0 && (existing?.thought_score ?? null) === -1;
    setNotConcerned(nc);
    prevScoresRef.current = null;
  }, [answers, currentQuestion]);

  useEffect(() => {
    if (!currentQuestion) return;

    const frame = window.requestAnimationFrame(() => {
      scrollElementBelowSticky(answerCardRef.current, stickyHeaderRef.current);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [categoryId, currentQuestion]);

  useEffect(() => {
    maybeAutoOpenPrompt({
      answeredQuestionsCount,
      source: 'question_answered_threshold'
    });
  }, [answeredQuestionsCount, maybeAutoOpenPrompt]);

  const canNext = currentQuestion && shareScore !== null && burdenScore !== null && thoughtScore !== null && !isSaving;

  const onToggleNotConcerned = (next) => {
    if (next) {
      // Save current choices so user can revert
      prevScoresRef.current = { shareScore, burdenScore, thoughtScore };
      setNotConcerned(true);
      setShareScore(0);
      setBurdenScore(0);
      setThoughtScore(-1);
      return;
    }

    setNotConcerned(false);
    const prev = prevScoresRef.current;
    prevScoresRef.current = null;
    if (prev && (prev.shareScore !== 0 || prev.burdenScore !== 0 || prev.thoughtScore !== -1)) {
      setShareScore(prev.shareScore ?? null);
      setBurdenScore(prev.burdenScore ?? null);
      setThoughtScore(prev.thoughtScore ?? null);
    } else {
      setShareScore(null);
      setBurdenScore(null);
      setThoughtScore(null);
    }
  };

  const onSaveAndNext = async () => {
    if (!currentQuestion) return;
    setIsSaving(true);
    setError('');
    const payload = {
      question_uuid: currentQuestion.question_uuid,
      share_score: shareScore,
      burden_score: burdenScore,
      thought_score: thoughtScore
    };

    // Optimistic update
    setAnswers((prev) => ({
      ...(prev || {}),
      [payload.question_uuid]: {
        share_score: payload.share_score,
        burden_score: payload.burden_score,
        thought_score: payload.thought_score
      }
    }));

    try {
      await apiFetch('/api/answers/upsert', { token, method: 'POST', body: payload });
      const progressRefresh = refreshProgress().catch(() => undefined);

      if (index >= category.questions.length - 1) {
        await progressRefresh;
        navigateEmbedded('/questionnaire');
        return;
      }

      setIndex((i) => i + 1);
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const combinedError = error || questionnaireError || progressError;

  if (combinedError) {
    return (
      <div className="max-w-3xl mx-auto mt-10 p-6">
        <div className="rounded-[24px] border border-red-200/70 bg-red-50/90 p-4 text-red-700 dark:bg-red-900 dark:text-red-200">
          {combinedError}
        </div>
      </div>
    );
  }

  if (!category || !answers || questionnaireStatus === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ptcm-primary dark:border-blue-400"></div>
      </div>
    );
  }

  const answeredInCategory = category.questions.reduce((acc, q) => acc + (answers[q.question_uuid] ? 1 : 0), 0);
  const totalInCategory = category.questions.length;
  const categoryPercent = totalInCategory === 0 ? 0 : Math.round((answeredInCategory / totalInCategory) * 100);

  return (
    <div
      className="max-w-3xl mx-auto mt-3 px-3 space-y-3"
      style={{
        paddingBottom: pageBottomPadding
      }}
    >
      <div
        ref={stickyHeaderRef}
        className="sticky z-10 -mx-3 px-3 pt-2 space-y-2"
        style={{ top: 'var(--ptcm-sticky-top-offset)' }}
      >
        <div className="rounded-[24px] border border-ptcm-border/90 dark:border-gray-800/80 bg-white/92 dark:bg-gray-950/90 backdrop-blur px-3 py-3 shadow-ptcm-float">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs sm:text-sm shrink-0"
              onClick={() => navigateEmbedded('/questionnaire')}
            >
              ← Catégories
            </Button>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold uppercase tracking-[0.16em] text-ptcm-muted dark:text-gray-400">
                {category.label}
              </div>
            </div>
            <div className="shrink-0 text-xs font-semibold text-ptcm-muted dark:text-gray-300">
              {answeredInCategory}/{totalInCategory} • {categoryPercent}%
            </div>
          </div>
          <div className="mt-2">
            <ProgressBar percent={categoryPercent} heightClassName="h-1.5" />
          </div>
        </div>

        <div className="rounded-[28px] border border-ptcm-border/90 dark:border-gray-800/80 bg-white/95 dark:bg-gray-950/95 backdrop-blur px-4 py-4 shadow-ptcm-card">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-ptcm-primary dark:text-blue-400">
            Question actuelle
          </div>
          <div className="mt-2 text-lg sm:text-xl font-semibold leading-snug text-ptcm-ink dark:text-gray-100">
            {currentQuestion?.label}
          </div>
        </div>
      </div>

      <div ref={answerCardRef}>
        <Card>
          <CardContent className="p-4 space-y-3 text-left">
          <div className="rounded-[24px] border border-ptcm-border dark:border-gray-800 bg-ptcm-panel/35 dark:bg-gray-900 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-ptcm-ink dark:text-gray-100">
                  Je ne suis pas concerné·e
                </div>
                {notConcerned || isNotConcernedScores ? (
                  <div className="text-[11px] text-ptcm-muted dark:text-gray-400">
                    Les choix sont désactivés et cette tâche est exclue.
                  </div>
                ) : null}
              </div>
              <Switch checked={notConcerned || isNotConcernedScores} onCheckedChange={onToggleNotConcerned} disabled={isSaving} />
            </div>
          </div>

          <StepSlider
            label="Ce que je fais"
            value={shareScore}
            steps={shareSteps}
            onChange={setShareScore}
            leftHint="Pas moi"
            rightHint="100% moi"
            density="compact"
            disabled={notConcerned || isNotConcernedScores}
            fallbackLabel={(notConcerned || isNotConcernedScores) ? 'Non concerné·e' : null}
          />
          <StepSlider
            label="Comment ça me pèse"
            value={burdenScore}
            steps={burdenSteps}
            onChange={setBurdenScore}
            leftHint="Léger"
            rightHint="Lourd"
            density="compact"
            disabled={notConcerned || isNotConcernedScores}
            fallbackLabel={(notConcerned || isNotConcernedScores) ? 'Non concerné·e' : null}
          />
          <StepSlider
            label="Qui pense à ça"
            value={thoughtScore}
            steps={thoughtSteps}
            onChange={setThoughtScore}
            leftHint="Partenaire"
            rightHint="Moi"
            density="compact"
            disabled={notConcerned || isNotConcernedScores}
            fallbackLabel={(notConcerned || isNotConcernedScores) ? 'Personne' : null}
          />
          </CardContent>
        </Card>
      </div>

      {/* Sticky bottom actions (mobile-first) */}
      <div
        className="fixed left-0 right-0 border-t border-ptcm-border dark:border-gray-800 bg-ptcm-bg/92 dark:bg-gray-900/90 backdrop-blur"
        style={{ bottom: actionBarBottom }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={index === 0 || isSaving}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          >
            Précédent
          </Button>
          <Button className="flex-[2]" size="lg" disabled={!canNext} onClick={onSaveAndNext}>
            {isSaving ? 'Enregistrement...' : (index >= totalInCategory - 1 ? 'Terminer' : 'Suivant')}
          </Button>
        </div>
      </div>
    </div>
  );
}

